import { useState, useEffect } from 'react';
import { caddieAPI } from '../services/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { Card } from './ui/card';
import { Users, Phone, Mail, Trash2, DollarSign, Pencil, Save, X } from 'lucide-react';
import { toast } from 'sonner';

function ViewRegisteredCaddiesDialog({ open, onOpenChange, onSelectCaddie, onRegisterNew }) {
  const [caddies, setCaddies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, caddieId: null, caddieName: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      loadCaddies();
      setEditingId(null);
    }
  }, [open]);

  const loadCaddies = async () => {
    try {
      const response = await caddieAPI.getRegisteredCaddies();
      setCaddies(response.data.caddies);
    } catch (error) {
      toast.error('Failed to load registered caddies');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (e, caddieId, caddieName) => {
    e.stopPropagation();
    setDeleteConfirm({ open: true, caddieId, caddieName });
  };

  const handleDeleteConfirm = async () => {
    try {
      await caddieAPI.deleteRegisteredCaddie(deleteConfirm.caddieId);
      toast.success('Caddie removed successfully');
      setDeleteConfirm({ open: false, caddieId: null, caddieName: '' });
      loadCaddies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove caddie');
    }
  };

  const handleEditClick = (caddie) => {
    setEditingId(caddie.id);
    setEditForm({
      caddie_name: caddie.caddie_name,
      caddie_surname: caddie.caddie_surname,
      caddie_phone: caddie.caddie_phone,
      caddie_email: caddie.caddie_email || '',
      notes: caddie.notes || '',
    });
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        caddie_email: editForm.caddie_email && editForm.caddie_email.trim()
          ? editForm.caddie_email.trim()
          : null
      };
      
      await caddieAPI.updateRegisteredCaddie(editingId, payload);
      
      toast.success('Caddie updated successfully');
      setEditingId(null);
      loadCaddies();
    } catch (error) {
      const rawDetail = error.response?.data?.detail;

      let message = 'Failed to update caddie';

      if (typeof rawDetail === 'string') {
        message = rawDetail;
      } else if (Array.isArray(rawDetail)) {
        message = rawDetail
          .map((item) => item?.msg)
          .filter(Boolean)
          .join(', ');
      }

      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSendMoney = (caddie) => {
    onSelectCaddie(caddie);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-white text-slate-900 border border-slate-200 rounded-2xl shadow-2xl p-4 sm:p-6 gap-3 sm:max-w-xl max-h-[88vh]" data-testid="view-caddies-dialog">
          <DialogHeader className="text-left space-y-1">
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold text-slate-900">
              <Users className="w-5 h-5" />
              My Registered Caddies
            </DialogTitle>
            <DialogDescription className="text-slate-600">
              Quick access to your preferred caddies
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="text-center py-8">
              <p className="text-slate-600">Loading caddies...</p>
            </div>
          ) : caddies.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 mb-2">No registered caddies yet</p>
              <p className="text-sm text-slate-500">Click "Register Caddie" to add your first caddie</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="caddies-list">
              {caddies.map((caddie) => (
                <Card key={caddie.id} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all" data-testid={`caddie-card-${caddie.id}`}>
                  {editingId === caddie.id ? (
                    /* Edit Mode */
                    <div className="space-y-3" data-testid={`edit-form-${caddie.id}`}>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs text-slate-600">First Name</Label>
                          <Input
                            value={editForm.caddie_name}
                            onChange={(e) => setEditForm({ ...editForm, caddie_name: e.target.value })}
                            className="input-field h-9 text-sm"
                            data-testid="edit-name-input"
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-slate-600">Surname</Label>
                          <Input
                            value={editForm.caddie_surname}
                            onChange={(e) => setEditForm({ ...editForm, caddie_surname: e.target.value })}
                            className="input-field h-9 text-sm"
                            data-testid="edit-surname-input"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Phone</Label>
                        <Input
                          value={editForm.caddie_phone}
                          onChange={(e) => setEditForm({ ...editForm, caddie_phone: e.target.value })}
                          className="input-field h-9 text-sm"
                          data-testid="edit-phone-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Email (optional)</Label>
                        <Input
                          value={editForm.caddie_email}
                          onChange={(e) => setEditForm({ ...editForm, caddie_email: e.target.value })}
                          className="input-field h-9 text-sm"
                          data-testid="edit-email-input"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Notes (optional)</Label>
                        <Input
                          value={editForm.notes}
                          onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                          className="input-field h-9 text-sm"
                          placeholder="e.g. Preferred caddie at Durban CC"
                          data-testid="edit-notes-input"
                        />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <Button size="sm" onClick={handleSaveEdit} disabled={saving} className="btn-primary text-xs h-8 flex-1" data-testid="save-edit-btn">
                          <Save className="w-3 h-3 mr-1" />
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="text-xs h-8 flex-1" data-testid="cancel-edit-btn">
                          <X className="w-3 h-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg font-bold text-slate-900 mb-2">
                          {caddie.caddie_name} {caddie.caddie_surname}
                        </h3>
                        <div className="space-y-1 mb-3">
                          <div className="flex items-center gap-2 text-sm text-slate-600">
                            <Phone className="w-4 h-4" />
                            <span className="break-all">{caddie.caddie_phone}</span>
                          </div>
                          {caddie.caddie_email && (
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                              <Mail className="w-4 h-4" />
                              <span className="break-all">{caddie.caddie_email}</span>
                            </div>
                          )}
                        </div>
                        {caddie.payment_count > 0 && (
                          <div className="bg-emerald-50 rounded-lg p-2 mb-3">
                            <div className="flex items-center gap-2 text-sm">
                              <DollarSign className="w-4 h-4 text-emerald-700" />
                              <span className="text-emerald-900">
                                <strong>{caddie.payment_count}</strong> payments &bull; <strong>R{caddie.total_payments.toFixed(2)}</strong> total
                              </span>
                            </div>
                            {caddie.last_payment_at && (
                              <p className="text-xs text-emerald-700 mt-1">
                                Last payment: {new Date(caddie.last_payment_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        )}
                        {caddie.notes && (
                          <p className="text-sm text-slate-600 italic bg-slate-50 p-2 rounded">
                            "{caddie.notes}"
                          </p>
                        )}
                      </div>
                      <div className="flex w-36 shrink-0 flex-col gap-2 sm:w-40">
                        <Button size="sm" onClick={() => handleSendMoney(caddie)} className="btn-primary h-10 px-4 text-xs shadow-md" data-testid={`send-money-${caddie.id}`}>
                          Send Money
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleEditClick(caddie)} className="h-9 rounded-full border-slate-100 bg-white text-xs text-blue-600 shadow-sm hover:border-slate-200 hover:bg-white hover:shadow-md focus-visible:ring-emerald-900/20" data-testid={`edit-${caddie.id}`}>
                          <Pencil className="w-3 h-3 mr-1" />
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={(e) => handleDeleteClick(e, caddie.id, `${caddie.caddie_name} ${caddie.caddie_surname}`)} className="h-9 rounded-full border-slate-100 bg-white text-xs text-red-600 shadow-sm hover:border-slate-200 hover:bg-white hover:shadow-md focus-visible:ring-emerald-900/20" data-testid={`delete-${caddie.id}`}>
                          <Trash2 className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      </div>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}

          <div className="pt-4 border-t border-slate-100 flex gap-3">
            <Button
              onClick={() => { onOpenChange(false); onRegisterNew(); }}
              className="flex-1 btn-primary"
              data-testid="register-new-caddie-btn"
            >
              Find / Register Caddie
            </Button>
            <Button onClick={() => onOpenChange(false)} variant="outline" className="flex-1 rounded-full bg-white" data-testid="close-caddies-btn">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(o) => !o && setDeleteConfirm({ open: false, caddieId: null, caddieName: '' })}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Caddie?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{deleteConfirm.caddieName}</strong> from your registered caddies? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">
              Remove Caddie
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export default ViewRegisteredCaddiesDialog;
